import { useParams } from "react-router-dom";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Section } from "@/components/section";
import { UserChip } from "@/components/user-chip";
import { UserPickerDialog } from "@/components/user-selector";
import type { PickerUser } from "@/components/user-selector";
import AppMembersDialog from "./AppMembersDialog";
import { useApp, useAppMembers } from "@/hooks/apps";
import { useTabParam } from "@/hooks/tab-param";
import {
  groupUsersByRole,
  putFetcher,
  roleLabel,
  useAuthStore,
} from "@/lib/swr";
import { Chip } from "@/components/badge/chip";
import type { User } from "@/lib/swr";
import { useIsAdmin } from "@/hooks/auth";
import { ListEmpty } from "@/components/list/status-text";
import { EditAppDialog } from "./edit-app-dialog";
import { AppTabsSection } from "./app-tabs-section";
import { RcFlowSection } from "./rc-flow-section";
import { NotifyChatSection } from "./notify-chat-section";
import { AppWebhooksSection } from "./app-webhooks-section";
import { AppBotsSection } from "./app-bots-section";
import { VersionFlowSection } from "./version-flow-section";
import { RolloutDataSection } from "./rollout-data-section";
import { ChannelGateSection } from "./channel-gate-section";
import { ChannelFlowSection } from "./channel-flow-section";
import { refreshApps } from "./refresh-apps";
import { ArchiveAppDialog } from "./archive-app-dialog";
// App settings tab, split into sub-tabs (General / Flow / Channel flow /
// Notifications / Webhooks) as the sections piled up. Actions are gated —
// owners (any of them) and admins can manage; the server re-checks on every
// management endpoint; manager-only sub-tabs are hidden for everyone else.
// 人 + 职能角色 chip,设置页 Owner 用
const UserWithRoles = ({ user }: { user: User }) => (
  <div className="flex items-center gap-1.5">
    <UserChip user={user} />
    {(user.roles ?? []).map((r) => (
      <Chip key={r} tone="green" className="px-1.5">
        {roleLabel(r)}
      </Chip>
    ))}
  </div>
);

const AppSettings = () => {
  const { id } = useParams();
  const app = useApp(id);
  const isAdmin = useIsAdmin();
  const me = useAuthStore((store) => store.auth?.user);
  const ownerIds = (app?.ownerIds ?? []).map(String);
  const isOwner = !!me && ownerIds.includes(String(me.id));
  const canManage = isAdmin || isOwner;
  const [tab, setTab] = useTabParam("tab", "general");
  const { data: users } = useSWR<User[]>(["/users"]);
  const owners = (users ?? []).filter((u) => ownerIds.includes(String(u.id)));
  // Members endpoint is owner/admin only — don't fetch (and 403) for others.
  const { members } = useAppMembers(canManage && app ? app.id : "");

  const { trigger: saveOwners, isMutating: savingOwners } = useSWRMutation(
    `/apps/${id}/owners`,
    putFetcher,
    {
      onSuccess: () => {
        refreshApps();
      },
    }
  );

  if (!app) return null;

  // Manager-only sub-tabs are hidden entirely; force others back to General.
  const activeTab = canManage ? tab : "general";

  return (
    <div className="flex max-w-3xl flex-col gap-6">
      {canManage && (
        <Tabs value={activeTab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="general">General</TabsTrigger>
            <TabsTrigger value="members">Members</TabsTrigger>
            <TabsTrigger value="bots">Bots</TabsTrigger>
            <TabsTrigger value="flow">Flow</TabsTrigger>
            <TabsTrigger value="rc-flow">RC flow</TabsTrigger>
            <TabsTrigger value="channel-flow">Channel flow</TabsTrigger>
            <TabsTrigger value="notifications">Notifications</TabsTrigger>
            <TabsTrigger value="webhooks">Webhooks</TabsTrigger>
          </TabsList>
        </Tabs>
      )}

      {activeTab === "general" && (
        <>
          <Section
            title="App"
            action={canManage ? (
              <div className="flex gap-2">
                <EditAppDialog app={app} />
                <ArchiveAppDialog app={app} />
              </div>
            ) : undefined}
          >
            <div className="flex items-center gap-2 text-sm">
              <span className="font-medium">{app.name}</span>
              <span className="text-muted-foreground">#{app.id}</span>
              {app.key && (
                <Chip className="font-mono" title="PRD short-ID prefix">
                  {app.key}
                </Chip>
              )}
              {app.group && (
                <span className="rounded bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                  {app.group}
                </span>
              )}
              {app.isApp && <Chip>app</Chip>}
              {app.archived && <Chip>Archived</Chip>}
            </div>
          </Section>

          <Section
            title="Owners"
            action={
              // Creator becomes the first owner; any owner or an admin can add
              // co-owners, hand over, or step down (at least one must remain).
              canManage ? (
                <UserPickerDialog
                  multiple
                  selected={owners as PickerUser[]}
                  onConfirm={(us) =>
                    saveOwners({ ownerIds: us.map((u) => String(u.id)) })
                  }
                  buttonText={savingOwners ? "Saving…" : "Edit"}
                  title="Manage owners"
                  description="Owners manage members and settings of this app. Keep at least one."
                />
              ) : undefined
            }
          >
            {owners.length ? (
              <div className="flex flex-wrap gap-x-4 gap-y-2">
                {owners.map((o) => (
                  <UserWithRoles key={o.id} user={o} />
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                No owner — ask an admin to assign one.
              </p>
            )}
          </Section>

          {/* Tab switches are a manager knob — hidden for plain members. */}
          {canManage && <AppTabsSection app={app} />}
        </>
      )}

      {activeTab === "members" && canManage && (
        <Section title="Members" action={<AppMembersDialog app={app} />}>
          {members.length ? (
            <div className="flex flex-col gap-5">
              {groupUsersByRole(members).map((g) => (
                <div key={g.key} className="space-y-2">
                  <div className="flex items-center gap-1.5 text-sm font-medium">
                    {g.label}
                    <span className="text-xs font-normal text-muted-foreground">
                      {g.users.length}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-2">
                    {g.users.map((m) => (
                      <UserChip key={m.id} user={m} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <ListEmpty>No members yet.</ListEmpty>
          )}
        </Section>
      )}

      {activeTab === "bots" && canManage && <AppBotsSection app={app} />}

      {activeTab === "flow" && canManage && (
        <>
          <VersionFlowSection app={app} />
          <RolloutDataSection app={app} />
        </>
      )}

      {activeTab === "rc-flow" && canManage && <RcFlowSection app={app} />}

      {activeTab === "channel-flow" && canManage && (
        <>
          <ChannelFlowSection app={app} />
          <ChannelGateSection app={app} />
        </>
      )}

      {activeTab === "notifications" && canManage && (
        <NotifyChatSection app={app} />
      )}

      {activeTab === "webhooks" && canManage && (
        <AppWebhooksSection pid={String(app.id)} />
      )}
    </div>
  );
};

export default AppSettings;
